import { Star, Quote, Award } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { studioInfo, testimonials } from "../data/mock";

const Reviews = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-[#9FFF00] to-[#8AE600] text-black overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-10 h-10 text-black fill-black" />
              ))}
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">{studioInfo.rating} Rating</h1>
            <p className="text-xl font-medium">
              Hear from our amazing community of members who've transformed their lives at GetFit
            </p>
          </div>
        </div>
      </section>

      {/* Featured Review */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Award className="w-16 h-16 text-black mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Featured Review
              </h2>
              <p className="text-gray-600">What our members are saying</p>
            </div>

            <Card className="border-2 border-[#9FFF00] shadow-2xl">
              <CardContent className="p-12">
                <Quote className="w-16 h-16 text-black mb-6" />
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-black fill-[#9FFF00]" />
                  ))}
                </div>
                <p className="text-2xl text-gray-700 mb-8 italic leading-relaxed">
                  "{testimonials[0].text}"
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonials[0].image}
                    alt={testimonials[0].name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-bold text-xl text-gray-900">{testimonials[0].name}</p>
                    <p className="text-gray-600">GetFit Member</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* All Reviews */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              More Success Stories
            </h2>
            <p className="text-xl text-gray-600">Real transformations from real people</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="border-2 hover:border-[#9FFF00] transition-all hover:shadow-xl"
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-black fill-[#9FFF00]" />
                    ))}
                  </div>
                  <Quote className="w-10 h-10 text-black mb-4" />
                  <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">GetFit Member</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Our Track Record
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-black mb-2">
                  {studioInfo.rating}
                </div>
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-black fill-[#9FFF00]" />
                  ))}
                </div>
                <p className="text-gray-600 font-medium">Average Rating</p>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-black mb-2">500+</div>
                <p className="text-gray-600 font-medium">Transformations</p>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-black mb-2">98%</div>
                <p className="text-gray-600 font-medium">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#9FFF00] to-[#8AE600] text-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Write Your Own Success Story?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto font-medium">
            Join hundreds of satisfied members who've achieved their fitness goals at GetFit
          </p>
          <a
            href="/contact"
            className="inline-block bg-black text-[#9FFF00] hover:bg-gray-900 font-bold px-8 py-4 rounded-lg text-lg transition-colors"
          >
            Book Your Free Consultation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
